import { useContext, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { RequestLoader, ToastContext } from "../../../../hooks/context";
import { bulkRestock } from "../../../../services/inventory";
import {
  cleanObject,
  deepCopyFunction,
  extractDateOnly,
  getRandomIntInclusive,
} from "../../../../helpers";
import { uploadImage } from "../../../../services/media";
import { useGetSuppliers } from "../../../../hooks/api/queries/useInventory";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useGetAllChartOfAccounts } from "../../../../hooks/api/queries";
import { useGetInfo } from "../../../../hooks/useGetInfo";
import { pathConstants } from "../../../../routes/pathContants";

const reArrangeStockItems = (items) => {
  const newItems = items?.map((item) => {
    return {
      product_id: item?.product?.id || "",
      variant_id: item?.variant_id || "",
      name: item?.product?.name || "",
      apply_pricing_to_all_stores: items?.use_same_pricing,
      cost_price: item?.cost_price || "",
      pricings: [
        {
          price: item?.selling_price || "",
          discount: item?.discount || "",
          pricing_type: { id: 1, label: "Retail" },
        },
      ],
      expiry_date: item?.expiry_date ? extractDateOnly(item?.expiry_date) : "",
      barcode: item?.barcode || item?.tracking_number || "",
      tracking_number: item?.tracking_number || item?.barcode || "",
      quantity: item?.quantity || "",
      vat_rate: item?.vat || "",
      store_name: item?.store?.name || "",
      store_id: item?.store?.id || "",
    };
  });

  return newItems;
};
export const useBulkRestock = ({ restockDetails }) => {
  const { restock_id } = useParams();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const triggerToast = useContext(ToastContext);
  const { setRequestLoaderProgress } = useContext(RequestLoader);
  const { storeList } = useSelector((state) => state.profileDetailsReducer);
  const [currentStore, setCurrentStore] = useState(
    search.get("store_id") || null,
  );
  const [restockData, setRestockData] = useState(
    reArrangeStockItems(restockDetails?.items || []),
  );
  const [subtotal, setSubtotal] = useState(0);
  const [otherRestockInfo, setOtherRestockInfo] = useState({
    supplier_id: restockDetails?.suppliers?.[0]?.id || "",
    invoice_number: restockDetails?.invoice_number || "",
    order_number: restockDetails?.order_number || "",
    invoice_date: restockDetails?.invoice_date
      ? extractDateOnly(restockDetails?.invoice_date)
      : "",
    discount: restockDetails?.discount || "",
    vat: restockDetails?.vat || "",
    amount_paid: restockDetails?.amount_paid || "",
    payment_method: restockDetails?.payment_method || "",
    notes: restockDetails?.notes || "",
    invoice_url: restockDetails?.invoice_url || "",
    invoice_due_date: restockDetails?.invoice_due_date
      ? extractDateOnly(restockDetails?.invoice_due_date)
      : "",
    ledger_account_id: restockDetails?.ledger_account_id || "",
  });
  const [storeError, setStoreError] = useState("");
  const [isLoadingDocUpload, setIsLoadingDocUpload] = useState(false);
  const [modalViews, setModalViews] = useState({
    isWarning: false,
    isCreateSupplier: false,
  });
  const { getStoreInfo, getPricingInfo } = useGetInfo();
  const [detectedPricing, setDetectedPricing] = useState([]);

  const resetModalViews = (key, value = true) => {
    setModalViews({
      isCreateSupplier: false,
      isWarning: false,
      [key]: value,
    });
  };

  const handleAppendRestockData = (item) => {
    setRestockData((prevData) => [...prevData, item]);
  };

  const { isLoading: isLoadingBankAccountsList, data: bankAccounts } =
    useGetAllChartOfAccounts({
      sub_account_type: "cash_and_bank",
      supplier_id: otherRestockInfo?.supplier_id,
    });

  const handleInvoiceUpload = (document) => {
    setIsLoadingDocUpload(true);
    setRequestLoaderProgress(getRandomIntInclusive(10, 50));
    const formData = new FormData();
    formData.append("file", document);
    uploadImage({ formData })
      .then((res) => {
        setOtherRestockInfo((prevState) => ({
          ...prevState,
          invoice_url: res?.data?.data?.url,
        }));
        setIsLoadingDocUpload(false);
        triggerToast("Document successfully uploaded", "success");
      })
      .finally(() => setRequestLoaderProgress(100));
  };

  const {
    data: suppliers,
    isLoading: isFetchingSupplier,
    refetch: handleFetchSuppliers,
  } = useGetSuppliers("?per_page=1000");

  const calculateSubtotal = () => {
    const subTotal = restockData.reduce((prev, curr) => {
      if (curr?.quantity === null) {
        curr.quantity = 0;
      }
      return prev + curr?.quantity * curr?.cost_price;
    }, 0);

    setSubtotal(subTotal);
  };

  const filterByStore = (value) => {
    search.set("store_id", value);
    setSearch(search, { replace: true });
  };

  const getSubmisionData = (newDetectedPricing) => {
    // clean up restock data
    const newRestockData = deepCopyFunction(restockData);
    const rearrangedData = newRestockData?.map((restockDataItem) => {
      const pricings = [
        cleanObject({
          price: restockDataItem?.pricings[0]?.price,
          discount_rate: restockDataItem?.pricings[0]?.discount_rate,
          pricing_type_id: restockDataItem?.pricings[0]?.pricing_type?.id,
        }),
      ];

      if (
        Number(restockDataItem?.cost_price) >
        Number(restockDataItem?.pricings[0]?.price)
      )
        newDetectedPricing.push({
          store_name: restockDataItem?.store_name,
          store_currency: getStoreInfo(restockDataItem?.store_id)
            ?.currency_code,
          cost_price: restockDataItem?.cost_price,
          selling_price: restockDataItem?.pricings[0]?.price,
          pricing_name: getPricingInfo(
            restockDataItem?.pricings[0]?.pricing_type?.id || 1,
          )?.label,
        });

      return cleanObject({ ...restockDataItem, pricings }, [
        "storeList",
        "errors",
      ]);
    });

    return rearrangedData;
  };

  const { mutate: restockItems, isLoading: isRestocking } = useMutation(
    (data) => bulkRestock(data),
  );
  const restockProducts = (isDraft = false) => {
    if (restockData?.length === 0)
      return triggerToast("You must restock at least one item", "warning");

    let newDetectedPricing = [];

    const rearrangedData = getSubmisionData(newDetectedPricing);

    // return a warning modal
    if (newDetectedPricing?.length > 0 && !modalViews?.isWarning) {
      setDetectedPricing(newDetectedPricing);
      return resetModalViews("isWarning", true);
    }
    resetModalViews("isWarning", false);
    restockItems(
      {
        data: cleanObject({
          restock_id,
          restock_data: rearrangedData,
          invoice_data: cleanObject({ ...otherRestockInfo }),
        }),
        isDraft,
      },
      {
        onSuccess: () => {
          setRestockData([]);
          setOtherRestockInfo({
            supplier_id: "",
            invoice_number: "",
            order_number: "",
            invoice_date: "",
            discount: "",
            vat: "",
            amount_paid: "",
            payment_method: "",
            notes: "",
            invoice_url: "",
            invoice_due_date: "",
          });
          triggerToast("Bulk Restocked Successfully", "success");
          queryClient.invalidateQueries("getRestock");
          if (restock_id)
            queryClient.invalidateQueries(
              "getDraftRestockDetails",
              `${restock_id}`,
            );

          navigate(
            isDraft === true
              ? pathConstants.INVENTORY_RESTOCK_DRAFTS
              : pathConstants.INVENTORY_RESTOCK_LOGS,
          );
        },
      },
    );
  };

  useEffect(() => {
    calculateSubtotal();
  }, [restockData]);

  return {
    isLoadingDocUpload,
    isRestocking,
    restockData,
    suppliers: suppliers || [],
    isLoading: isFetchingSupplier,
    otherRestockInfo,
    subtotal,
    totalPrice:
      subtotal -
      (otherRestockInfo?.discount === ""
        ? 0
        : Number(otherRestockInfo?.discount)) +
      (otherRestockInfo?.vat === ""
        ? 0
        : (Number(otherRestockInfo?.vat) * subtotal) / 100),
    storeList,
    currentStore,
    storeError,
    modalViews,
    detectedPricing,
    search,
    resetModalViews,
    setCurrentStore,
    setStoreError,
    setOtherRestockInfo,
    restockProducts,
    setRestockData,
    handleAppendRestockData,
    handleFetchSuppliers,
    handleInvoiceUpload,
    filterByStore,
    isLoadingBankAccountsList,
    bankAccounts: bankAccounts?.data,
  };
};
